import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface DeletePolicyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  policyName: string
  onConfirm: () => void
  isLoading?: boolean
}

/**
 * DeletePolicyDialog confirms single policy deletion
 *
 * Features:
 * - AlertDialog for destructive action confirmation
 * - Displays policy name being deleted
 * - Warning message about permanent deletion
 * - Cancel/Delete buttons with loading state
 */
export function DeletePolicyDialog({
  open,
  onOpenChange,
  policyName,
  onConfirm,
  isLoading = false,
}: DeletePolicyDialogProps) {
  const handleConfirm = () => {
    onConfirm()
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-policy-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Policy</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">"{policyName}"</span>?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          This action cannot be undone. The policy and all associated data will
          be permanently removed.
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="delete-confirm-button"
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
